import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import Select from "react-select";
import pestimage from "assets/img/SPB_main.jpg";
import highlightsimage from "assets/img/IDSurvey_2022_highlights_map.jpg";
import resourcesimage from "assets/img/additional-resources-2024.png";
import Hero from "../Hero/Hero.jsx";
import "./NationalReports.css";

import {
  getAllYearsRpt,
  getPestSummaryMortalityAcres,
  getPestSummaryOtherAcres,
  getPestSummarySurveyedAcres,
} from "../../services/pestTableService";

function NationalReports() {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedYear, setSelectedYear] = useState();
  const [mortalityAcres, setMortalityAcres] = useState();
  const [otherAcres, setOtherAcres] = useState();
  const [chartVersion, setChartVersion] = useState(true);
  const [announcement, setAnnouncement] = useState("");
  const years = useRef([
    {
      value: 2021,
      label: "2021",
      isDisabled: false,
    },
  ]);
  const highlightLink = useRef(
    "https://www.fs.usda.gov/foresthealth/applied-sciences/mapping-reporting/detection-surveys.shtml#idsmaps"
  );
  const reportLink = useRef();
  const chart1 = useRef();
  const chartRef1 = useRef(null);
  const tAcresY1 = useRef("");
  const tAcresY2 = useRef("");
  const tAcresY3 = useRef("");
  const tAcresY4 = useRef("");
  const tAcresY5 = useRef("");
  const tAcresN1 = useRef("");
  const tAcresN2 = useRef("");
  const tAcresN3 = useRef("");
  const tAcresN4 = useRef("");
  const tAcresN5 = useRef("");
  const oAcres = useRef([0, 0, 0, 0, 0]);
  const config1 = useRef();

  // Initial load
  useEffect(() => {
    window.scroll(0, 0);
    setAnnouncement("You are now on the National Reports page");
    createTreeMortalityChart();
    getAllYearsRpt().then((result) => {
      const years1 = result.data;
      years.current = years1.slice(0, 5);
      setSelectedYear(years.current[0]);
    });
    document.title = "Forest Pest Conditions - National Report";
  }, []);

  // When selected year changes
  useEffect(() => {
    if (selectedYear) {
      reportLink.current =
        "https://www.fs.usda.gov/foresthealth/publications/ConditionsReport_" +
        selectedYear.value +
        ".pdf";
      highlightLink.current =
        "https://www.fs.usda.gov/foresthealth/technology/pdfs/IDSurvey_" +
        selectedYear.value +
        "_highlights.pdf";
      getMortalityData(selectedYear.value).then(([mort, mortAll, othAll]) => {
        const rchartMortality = mort.data;
        const allTreeDamage = mortAll.data;
        const allSurveyed = othAll.data;

        let mortality =
          rchartMortality.data[0] != null
            ? rchartMortality.data[0].fAcres.toString()
            : "0";
        mortality = mortality + " million";

        let other =
          allTreeDamage.data[0] != null
            ? allTreeDamage.data[0].Acres.toString()
            : "0";
        other =
          other.substring(0, other.length - 6) +
          "." +
          other.substring(other.length - 6, other.length - 5) +
          " million";

        setMortalityAcres(mortality);
        setOtherAcres(other);

        tAcresY1.current = "NA";
        tAcresY2.current = "NA";
        tAcresY3.current = "NA";
        tAcresY4.current = "NA";
        tAcresY5.current = "NA";

        tAcresN1.current = 0;
        tAcresN2.current = 0;
        tAcresN3.current = 0;
        tAcresN4.current = 0;
        tAcresN5.current = 0;

        oAcres.current = 0;

        tAcresY1.current =
          rchartMortality.data[0] != null ? rchartMortality.data[0].Year : "NA";
        tAcresY2.current =
          rchartMortality.data[1] != null ? rchartMortality.data[1].Year : "NA";
        tAcresY3.current =
          rchartMortality.data[2] != null ? rchartMortality.data[2].Year : "NA";
        tAcresY4.current =
          rchartMortality.data[3] != null ? rchartMortality.data[3].Year : "NA";
        tAcresY5.current =
          rchartMortality.data[4] != null ? rchartMortality.data[4].Year : "NA";

        tAcresN1.current = (
          (rchartMortality.data[0] != null
            ? rchartMortality.data[0].Acres
            : 0) / 1000000
        ).toPrecision(2);
        tAcresN2.current = (
          (rchartMortality.data[1] != null
            ? rchartMortality.data[1].Acres
            : 0) / 1000000
        ).toPrecision(2);
        tAcresN3.current = (
          (rchartMortality.data[2] != null
            ? rchartMortality.data[2].Acres
            : 0) / 1000000
        ).toPrecision(2);
        tAcresN4.current = (
          (rchartMortality.data[3] != null
            ? rchartMortality.data[3].Acres
            : 0) / 1000000
        ).toPrecision(2);
        tAcresN5.current = (
          (rchartMortality.data[4] != null
            ? rchartMortality.data[4].Acres
            : 0) / 1000000
        ).toPrecision(2);

        oAcres.current = [
          (allSurveyed.data[4].Acres / 1000000).toPrecision(2),
          (allSurveyed.data[3].Acres / 1000000).toPrecision(2),
          (allSurveyed.data[2].Acres / 1000000).toPrecision(2),
          (allSurveyed.data[1].Acres / 1000000).toPrecision(2),
          (allSurveyed.data[0].Acres / 1000000).toPrecision(2),
        ];

        config1.current.data.labels = [
          tAcresY5.current,
          tAcresY4.current,
          tAcresY3.current,
          tAcresY2.current,
          tAcresY1.current,
        ];
        config1.current.data.datasets[0].data = [
          tAcresN5.current,
          tAcresN4.current,
          tAcresN3.current,
          tAcresN2.current,
          tAcresN1.current,
        ];

        config1.current.data.datasets[1].data = oAcres.current;
        chart1.current.update();
      });
    }
  }, [selectedYear]);

  function getMortalityData(selYear) {
    const filter = { years: [selYear], damageTypeId: 92 };
    const filterAll = { years: [selYear], damageTypeId: 93 };
    return Promise.all([
      getPestSummaryMortalityAcres(filter),
      getPestSummaryOtherAcres(filterAll),
      getPestSummarySurveyedAcres(filterAll),
    ]);
  }

  const createTreeMortalityChart = () => {
    const myChartRef1 = chartRef1.current.getContext("2d");

    config1.current = {
      type: "bar",
      data: {
        //Bring in data
        labels: [
          tAcresY5.current,
          tAcresY4.current,
          tAcresY3.current,
          tAcresY2.current,
          tAcresY1.current,
        ],
        datasets: [
          {
            label: "Acres (million)",
            backgroundColor: "#7F9563",
            borderColor: "#7F9563",
            yAxisID: "A",
            data: [
              tAcresN5.current,
              tAcresN4.current,
              tAcresN3.current,
              tAcresN2.current,
              tAcresN1.current,
            ],
          },
          {
            label: "Surveyed Area (million)",
            data: oAcres.current,
            fill: false,
            backgroundColor: "#595959",
            borderColor: "#595959",
            tension: 0.4,
            yAxisID: "B",
            // Changes this dataset to become a line
            type: "line",
          },
        ],
      },
      options: {
        //Customize chart options
        scales: {
          A: {
            id: "A",
            type: "linear",
            beginAtZero: true,
            position: "left",
            max: 12,
            ticks: {
              max: 12,
              min: 0,
            },
          },
          B: {
            id: "B",
            type: "linear",
            beginAtZero: true,
            position: "right",
            max: 600,
            ticks: {
              max: 600,
              min: 0,
            },
          },
        },
        plugins: {
          tooltip: {
            enabled: false,
          },
        },
      },
    };

    if (chart1.current) {
      chart1.current.destroy();
    }
    chart1.current = new Chart(myChartRef1, config1.current);
    setDataLoaded(true);
  };

  return (
    <>
      <Hero value="National Reports" />
      <main className="grid-container national-reports" id="main-content">
        <div className="grid-row">
          <div className="grid-column">
            <h1 className="main-heading">
              Forest Insect and Disease Conditions
            </h1>
          </div>
        </div>
        <div className="visually-hidden" aria-live="polite">
          {announcement}
        </div>
        <div className="grid-row">
          <h2 style={{ color: "#216e1f" }}>Results Listed by Year</h2>
        </div>
        <div className="grid-row">
          <div className="tablet:grid-col-2 mobile-lg:grid-col-6">
            {dataLoaded && (
              <Select
                className="react-select select-year"
                placeholder="Select Year"
                aria-label="Select a year. Press the down arrow to expand the option list. Press delete
                to clear the current selection."
                name="singleSelect"
                value={selectedYear}
                onChange={(value) => setSelectedYear(value)}
                options={years.current}
                hideselectedoptions="true"
              />
            )}
            <br />
          </div>
        </div>
        {dataLoaded && selectedYear && mortalityAcres && otherAcres && (
          <div className="grid-row center national-stats">
            <div
              aria-live="polite"
              aria-atomic="true"
              className="stat-section tablet:grid-col mobile-lg:grid-col-12 text-center"
            >
              <div className="stat-section-header">Year</div>
              <div className="stat-section-value">
                {selectedYear ? selectedYear.value : ""}
              </div>
            </div>
            <div
              aria-live="polite"
              aria-atomic="true"
              className="stat-section tablet:grid-col mobile-lg:grid-col-12 text-center"
            >
              <div className="stat-section-header">With Dead Trees (Acres)</div>
              <div className="stat-section-value">{mortalityAcres}</div>
            </div>
            <div
              aria-live="polite"
              aria-atomic="true"
              className="stat-section tablet:grid-col mobile-lg:grid-col-12 text-center"
            >
              <div className="stat-section-header">All Tree Damage (Acres)</div>
              <div className="stat-section-value">{otherAcres}</div>
            </div>
          </div>
        )}
        <div className="grid-row">
          <h2>All Tree Mortality</h2>
        </div>
        <div className="grid-row">
          <div className="national-reports-section margin-right tablet:grid-col-6 mobile-lg:grid-12">
            <p>
              Total acres with mortality from forest health damage detection
              surveys are summarized from current year's observations only and
              are not cumulative.
            </p>
            <p>
              Insect and disease surveys for 2020 were impacted by COVID-19 and
              some regions and states were unable to conduct typical surveys.
              Therefore, other remote sensing products and additional ground
              surveys were used to supplement data gathered by aerial surveys.
              Overall, there was a 50% reduction in acres surveyed this year
              with only 215M acres in 2020; down from 500M acres surveyed in
              2019.
            </p>
          </div>
          <div className="national-reports-section tablet:grid-col-1 text-center mobile-lg:grid-col-0 mobile-disappear"></div>
          <div className="national-reports-section tablet:grid-col-5 text-center mobile-lg:grid-col-12">
            <div className="chart-type-buttons">
              <button
                onClick={() => {
                  setChartVersion(true);
                }}
                className={
                  "first " + (chartVersion ? "selected" : "unselected")
                }
                aria-label="Select chart version of trend data"
              >
                Chart version
              </button>
              <button
                onClick={() => {
                  setChartVersion(false);
                }}
                className={!chartVersion ? "selected" : "unselected"}
                aria-label="Select tabular version of trend data"
              >
                Tabular version
              </button>
            </div>
            <div
              className="chart-table-region"
              aria-live="polite"
              aria-atomic="true"
            >
              <b>
                National tree mortality trend {chartVersion ? "chart" : "table"}
              </b>
              <canvas
                id="myChart1"
                aria-label="National tree mortality trend chart"
                ref={chartRef1}
                tabIndex="0"
                role="img"
                className={chartVersion ? "visible" : "hidden"}
              >
                Select Tabular version button to display chart data in tabular
                form
              </canvas>
              <table
                aria-label="National tree mortality trend table"
                className={
                  "usa-table pest-trend-table " +
                  (chartVersion ? "hidden" : "visible")
                }
              >
                <thead>
                  <tr>
                    <th scope="col" aria-label="Year">
                      Year
                    </th>
                    <th
                      scope="col"
                      aria-label="National tree mortality in millions of acres"
                    >
                      National tree mortality
                      <br />
                      (million acres)
                    </th>
                    <th
                      scope="col"
                      aria-label="Surveyed area in millions of acres"
                    >
                      Surveyed area
                      <br />
                      (million acres)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{tAcresY5.current}</td>
                    <td>{tAcresN5.current}</td>
                    <td>{(oAcres.current[0] * 1000000) / 1000000}</td>
                  </tr>
                  <tr>
                    <td>{tAcresY4.current}</td>
                    <td>{tAcresN4.current}</td>
                    <td>{(oAcres.current[1] * 1000000) / 1000000}</td>
                  </tr>
                  <tr>
                    <td>{tAcresY3.current}</td>
                    <td>{tAcresN3.current}</td>
                    <td>{(oAcres.current[2] * 1000000) / 1000000}</td>
                  </tr>
                  <tr>
                    <td>{tAcresY2.current}</td>
                    <td>{tAcresN2.current}</td>
                    <td>{(oAcres.current[3] * 1000000) / 1000000}</td>
                  </tr>
                  <tr>
                    <td>{tAcresY1.current}</td>
                    <td>{tAcresN1.current}</td>
                    <td>{(oAcres.current[4] * 1000000) / 1000000}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <hr className="mobile-disappear" />
        <div className="grid-row">
          <h2>
            Major Forest Insect and Disease Conditions in the United States
          </h2>
          <div className="grid-row">
            <div className="national-reports-section tablet:grid-col-6 mobile-lg:grid-col-12 text-center">
              <img
                src={pestimage}
                width="100%"
                id="beetle-image"
                alt="Southern pine beetle in flight"
              ></img>
              <b>Southern pine beetle in flight</b>
              <div>Photo: USDA Forest Service, FHAAST</div>
            </div>
            <div className="national-reports-section margin-left tablet:grid-col-6 mobile-lg:grid-col-12">
              <p>
                The annual forest conditions report provides information
                generated through the combined efforts of U.S. Department of
                Agriculture (USDA), Forest Service employees; State agencies;
                and other partners. Their dedication to the monitoring,
                detection, suppression, treatment, and management of our
                forested lands for insects and disease makes this report
                possible.
              </p>
              <p>
                <a
                  href="https://www.fs.usda.gov/science-technology/forest-health-protection/publications-reports/forest-health-protection-publications"
                  title="Forest Health Protection Publications website"
                  target="_blank"
                  rel="noreferrer"
                  className="usa-link"
                >
                  Visit the Forest Health Protection Publications website for
                  the latest condition report
                </a>
              </p>
            </div>
          </div>
        </div>
        <hr className="mobile-disappear" />
        <div className="grid-row">
          <h2>National Highlights of Tree Mortality</h2>
        </div>
        <div className="grid-row">
          <div className="national-reports-section margin-right tablet:grid-col-6 mobile-lg:grid-col-12">
            <p>
              Forest Health Protection highlights of tree mortality and other
              damage from insects and diseases. Approximately 500 million acres
              surveyed annually.
            </p>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.fs.usda.gov/science-technology/data-tools-and-products/fhp-mapping-reporting/detection-surveys"
              title="Visit website with additional National Insect and Disease Detection Survey maps"
              className="usa-link"
            >
              View additional National Insect and Disease Detection Survey maps
            </a>
          </div>
          <div className="national-reports-section tablet:grid-col-6 mobile-lg:grid-col-12 text-center">
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.fs.usda.gov/foresthealth/applied-sciences/mapping-reporting/detection-surveys.shtml#idsmaps"
              title="Visit website with additional National Insect and Disease Detection Survey maps"
            >
              <img
                src={highlightsimage}
                id="img1"
                alt="2022 National Forest Insect and Disease Detection Survey Map"
              />
            </a>
          </div>
        </div>
        <hr className="mobile-disappear" />
        <div className="grid-row">
          <h2>Additional Resources</h2>
        </div>
        <div className="grid-row">
          <div className="national-reports-section additional-resources tablet:grid-col-6 mobile-lg:grid-col-12 text-center">
            <a
              href="https://www.fs.usda.gov/science-technology/forest-health-protection"
              target="_blank"
              rel="noreferrer"
              title="Visit Forest Health Protection website"
            >
              <img
                src={resourcesimage}
                id="addtional-resources"
                alt="Screenshot of Forest Health Protection website"
              />
            </a>
          </div>
          <div className="national-reports-section margin-left tablet:grid-col-6 mobile-lg:grid-col-12">
            <p>
              We have over 150 specialists in the areas of forest entomology,
              forest pathology, invasive plants, pesticide use, survey and
              monitoring, suppression and control, assessment and applied
              sciences, and other forest health-related services.
            </p>
            <p>
              <a
                href="https://www.fs.usda.gov/science-technology/forest-health-protection"
                target="_blank"
                className="usa-link"
                rel="noreferrer"
                title="Visit Forest Health Protection website"
              >
                Forest Health Protection website
              </a>
            </p>
            <p>
              In addition to the Forest Health Protection (FHP) program, other
              USDA Forest Service programs and offices monitor, model, and
              research forest health-related issues. These programs and offices
              work together with FHP to address threats to our nation's forests
              comprehensively. Check out the links below to learn more:
            </p>
            <p>
              <a
                href="https://www.fs.usda.gov/managing-land/fire/resilient-landscapes"
                target="_blank"
                className="usa-link"
                rel="noreferrer"
                title="Visit USFS Fire and Aviation Management website"
              >
                USFS Fire and Aviation Management website
              </a>
              <br />
              <a
                href="https://www.fs.usda.gov/managing-land/sustainability-and-climate/data-dashboard"
                target="_blank"
                className="usa-link"
                rel="noreferrer"
                title="Visit USFS Office of Sustainability and Climate website"
              >
                USFS Office of Sustainability and Climate website
              </a>
              <br />
              <a
                href="https://fia-usfs.hub.arcgis.com/"
                target="_blank"
                className="usa-link"
                rel="noreferrer"
                title="Visit USFS Forest Inventory and Analysis website"
              >
                USFS Forest Inventory and Analysis website
              </a>
              <br />
              <a
                href="https://research.fs.usda.gov/pnw/centers/wwetac"
                target="_blank"
                className="usa-link"
                rel="noreferrer"
                title="Visit the USFS Western Wildland Environmental Threat Assessment Center website"
              >
                USFS Western Wildland Environmental Threat Assessment Center
              </a>
              <br />
              <a
                href="https://research.fs.usda.gov/centers/efetac"
                target="_blank"
                className="usa-link"
                rel="noreferrer"
                title="Visit the USFS Eastern Forest Environmental Threat Assessment Center website"
              >
                USFS Eastern Forest Environmental Threat Assessment Center
              </a>
            </p>
          </div>
        </div>
      </main>
    </>
  );
}

export default NationalReports;
